// src/hooks/useFlowHandlers.js
import { useCallback } from 'react';
import { buildFlowNode, buildNodeDataFromStep } from '../utils/flowNodeData';
import { createWorkflowStep } from '../utils/workflowStepFactory';
import { appendNavigationAction } from '../utils/workflowGraphMutations';

export default function useFlowHandlers({
  workflow,
  setWorkflow,
  setNodes,
  screenToFlowPosition,
  getNewNodeId,
  getExistingNodeIds,
  showDetailedContent,
  selectedAddNodeType,
}) {
  // If user draws an edge from node->node — workflow_steps is the source of truth; edges sync via effect.
  const handleConnect = useCallback(
    (params) => {
      setWorkflow((prev) => {
        if (!prev) return prev;
        const sourceExists = prev.workflow_steps.some((step) => step.id === params.source);
        if (!sourceExists) return prev;
        return appendNavigationAction(prev, params.source, params.target);
      });
    },
    [setWorkflow]
  );

  // If user tries to drop an edge on empty space -> create new node
  const handleConnectEnd = useCallback(
    (event, connection) => {
      if (!connection.isValid) {
        if (!workflow) return;

        const id = getNewNodeId(getExistingNodeIds(workflow.workflow_steps));
        const { clientX, clientY } =
          'changedTouches' in event ? event.changedTouches[0] : event;

        const flowPos = screenToFlowPosition({ x: clientX, y: clientY });

        const newStep = createWorkflowStep(selectedAddNodeType, id);
        setNodes((nds) => [...nds, buildFlowNode(newStep, flowPos, showDetailedContent)]);

        setWorkflow((prev) => {
          if (!prev) return prev;

          let next = {
            ...prev,
            workflow_steps: [...prev.workflow_steps, newStep],
          };

          if (connection.fromNode) {
            next = appendNavigationAction(next, connection.fromNode.id, id);
          }

          return next;
        });
      }
    },
    [
      workflow,
      screenToFlowPosition,
      setNodes,
      setWorkflow,
      showDetailedContent,
      getExistingNodeIds,
      getNewNodeId,
      selectedAddNodeType,
    ]
  );

  // Update node label after user modifies heading in the EditorSidebar
  const updateSingleNode = (nodeId, updatedWorkflow) => {
    const view = updatedWorkflow.workflow_steps.find((v) => v.id === nodeId);
    if (!view) return;
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: buildNodeDataFromStep(view, n.data?.showDetailedContent ?? showDetailedContent),
            }
          : n
      )
    );
  };

  // Called from EditorSidebar to handle node/edge updates (persistence is explicit via saveWorkflow in the sidebar)
  const handleWorkflowChange = (updatedWorkflow, changedItem) => {
    setWorkflow(updatedWorkflow);

    if (changedItem?.type === 'node') {
      updateSingleNode(changedItem.id, updatedWorkflow);
    }
  };

  return {
    handleConnect,
    handleConnectEnd,
    handleWorkflowChange,
  };
}
